import {DataTable, DataTableSortStatus} from "mantine-datatable";
import type { DataTableColumn } from "mantine-datatable";
import {TextInput, useMantineTheme, Stack} from "@mantine/core";
import { useMediaQuery } from '@mantine/hooks';
import FileFolderCard from './FileFolderCard';
import classes from "./TableFolders.module.css";
import {useEffect, useState} from "react";
import TableFileName from "./TableFileName";

import TableStatusText from "./TableStatusText";
import sortBy from 'lodash/sortBy';
import {IconSearch} from '@tabler/icons-react';
import TableListMessages from "./TableListMessages";
import TableLoadingRow from "./TableLoadingRow";
import {LoadingState} from "../interfaces/LoadingState";
import {TableFilesRow} from "../interfaces/TableFilesRow";


interface TableFolderProps {
    columns: DataTableColumn<TableFilesRow>[];
    rows: TableFilesRow[];
    loading: any;
    selected?: any[];
    setSelected?: any;
    /**
     * Provide a search string from the parent component. When set, the internal search box is hidden
     *  and this string is used for filtering.
     */
    externalSearch?: string;
}

function TableFolders({columns, rows, loading, selected, setSelected, externalSearch}: TableFolderProps) {

    const theme = useMantineTheme();
    const isMobile = useMediaQuery(`(max-width: ${theme.breakpoints.sm})`);



    //const [bodyRef] = useAutoAnimate<HTMLTableSectionElement>();

    const [sortStatus, setSortStatus] = useState<DataTableSortStatus<TableFilesRow>>({ columnAccessor: 'name', direction: 'asc' });
    const [records, setRecords] = useState(sortBy(rows, 'name') as TableFilesRow[]);
    const [search, setSearch] = useState(''); // local search state when search input is rendered

    // Sort Whenever sortStatus Changes
    useEffect(() => {
        const data = sortBy(rows, sortStatus.columnAccessor) as TableFilesRow[];
        setRecords(sortStatus.direction === 'desc' ? data.reverse() as TableFilesRow[] : data);
    }, [sortStatus]);

    useEffect(() => {
        setRecords(sortBy(rows, 'name'))
    }, [rows])

    const effectiveSearch = externalSearch !== undefined ? externalSearch : search;

    if(isMobile){
        if(loading){
            const placeholderColumns = [
                { accessor: 'placeholder', title: '' }
            ];
            return (
                <DataTable<any>
                    className={classes.noHeader}
                    columns={placeholderColumns}
                    records={[{ placeholder: '' }]}
                    fetching={true}
                    loaderType="dots"
                    withTableBorder={false}
                    minHeight={140}
                />
            );
        }
        const filtered = records.filter(r => r.name.toLowerCase().includes(effectiveSearch.toLowerCase()));
        return (
            <Stack>
                {filtered.map(r => {
                    const isSel = (selected as TableFilesRow[]).some(s => s.name === r.name);
                    const disabled = r.status === false;
                    const toggleSelect = (checked: boolean) => {
                        if(checked){
                            setSelected([...(selected as TableFilesRow[]), r]);
                        } else {
                            setSelected((selected as TableFilesRow[]).filter(s => s.name !== r.name));
                        }
                    }
                    return (
                        <FileFolderCard key={r.name} row={r} selected={isSel} disabled={disabled} toggleSelect={toggleSelect} />
                    );
                })}
            </Stack>
        );
    }

    return (
        <>
            {externalSearch === undefined && (
            <TextInput
                classNames={{
                    root: classes.searchInputTextRoot,
                    input: classes.searchInputTextInput,
                    wrapper: classes.searchInputTextWrapper
                }}
                radius={'lg'}
                style={{paddingBottom: '16px', flexBasis: '60%'}}
                placeholder="Search..."
                leftSection={<IconSearch size={16} />}
                value={search}
                onChange={(e) => setSearch(e.currentTarget.value)}
            />)}
            <DataTable<TableFilesRow>
                classNames={{
                    header: classes.tableHeader,
                    table: classes.tableTableCompact,
                }}
                rowStyle={(row, index) => ({
                    borderTop: `1px solid ${theme.colors.gray[0]}`,
                    borderBottom: `1px solid ${theme.colors.gray[0]}`
                })}
                highlightOnHover
                rowClassName={() => classes.rowHover}
                records={records.filter(r => r.name.toLowerCase().includes(effectiveSearch.toLowerCase()))}
                columns={columns}

                height={loading ? 180 : '100%'}
                selectedRecords={selected as TableFilesRow[]}
                onSelectedRecordsChange={setSelected}

                style={{
                    border: `2px solid ${theme.colors.gray[0]}`,
                    borderRadius: '12px',
                }}

                sortStatus={sortStatus}
                onSortStatusChange={(a) => setSortStatus(a)}

                idAccessor="name"

                fetching={loading}

                isRecordSelectable = {record => record.status !== false}

                minHeight={180}

                selectionCheckboxProps={{
                    size: 'sm',
                    radius: 'sm',
                    color: theme.colors.green[2],
                    styles: {
                        inner: {
                            width: '23px',
                            height: '23px',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',  
                        },
                        input: {
                            width: '24px',
                            height: '24px',
                            border: `1px solid ${theme.colors.gray[6]}`,
                        },
                        icon: {
                            width: '14px',
                            height: '14px',
                            color: 'black',
                        },
                    }
                }}

                defaultColumnRender={(row, _, accessor) => {
                    const data = row[accessor as keyof typeof row];
                    switch(accessor) {
                        case 'name': return <TableFileName text={data as string} />
                        case 'status': return <TableStatusText status={data as boolean} />
                        case 'messages': return <TableListMessages messages={data as string[]} />
                        case 'loading': return <TableLoadingRow loadingState={data as LoadingState} />
                        default: return (<>{data}</>)
                    }
                }}

                //bodyRef={bodyRef}
            />
        </>
    )

}

export default TableFolders;